// Auth & User Normalization utilities
// Provides functions to normalize user objects and derive display information

/**
 * Derives a full name from user object
 * Priority: firstName + lastName > name > email localpart
 * @param {Object} user - User object with potential name fields
 * @returns {string} Full name for the user
 */
export function deriveFullName(user) {
  if (!user) return 'Unknown User';
  
  // Try firstName + lastName
  const firstName = user.firstName || user.firstname || '';
  const lastName = user.lastName || user.lastname || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  
  // Try name field
  if (user.name && typeof user.name === 'string' && user.name.trim()) {
    return user.name.trim();
  }
  
  // Fall back to email localpart
  if (user.email && typeof user.email === 'string') {
    const match = user.email.match(/^([^@]+)@/);
    if (match) return match[1];
  }
  
  return 'Unknown User';
}

/**
 * Canonicalizes role values from backend to internal format
 * Maps: 'super admin' -> 'SA', 'executive' -> 'E', 'developer' -> 'DEV', 'manager' -> 'M',
 * 'admin' -> 'A', 'driver'/'employee' -> 'D'
 * @param {string|string[]|Object} rawRoles - Raw role data from backend
 * @returns {string[]} Array of canonical role codes (e.g., ['A'], ['E', 'D'])
 */
export function canonicalizeRoles(rawRoles) {
  // Handle various input formats
  let roles = [];
  
  if (Array.isArray(rawRoles)) {
    roles = rawRoles;
  } else if (typeof rawRoles === 'string') {
    roles = [rawRoles];
  } else if (rawRoles && typeof rawRoles === 'object') {
    // Handle object with role property
    if (rawRoles.role) {
      roles = Array.isArray(rawRoles.role) ? rawRoles.role : [rawRoles.role];
    } else if (rawRoles.roles) {
      roles = Array.isArray(rawRoles.roles) ? rawRoles.roles : [rawRoles.roles];
    }
  }
  
  // Map to canonical values
  const canonical = roles
    .map((r) => {
      if (!r) return null;
      const str = typeof r === 'string' ? r : String(r);
      const lower = str.toLowerCase().trim();

      // Map backend values
      if (
        lower === 'sa' ||
        lower === 'super admin' ||
        lower === 'superadmin' ||
        lower === 'super-admin' ||
        lower === 's'
      )
        return 'SA';
      if (lower === 'executive' || lower === 'e') return 'E';
      if (lower === 'developer' || lower === 'dev' || lower === 'c') return 'DEV';
      if (lower === 'manager' || lower === 'm') return 'M';
      if (lower === 'admin' || lower === 'a') return 'A';
      if (lower === 'employee' || lower === 'driver' || lower === 'd' || lower === 'dr')
        return 'D';

      // If it's already uppercase single/double letter, keep it
      if (/^[A-Z]{1,3}$/.test(str)) return str;

      return null;
    })
    .filter(Boolean);
  
  // De-duplicate
  const unique = Array.from(new Set(canonical));
  
  // Fallback to ['A'] if empty
  return unique.length > 0 ? unique : ['A'];
}

/**
 * Normalizes a user object to a stable internal shape
 * @param {Object} raw - Raw user object from backend or storage
 * @returns {Object} Normalized user object with consistent fields
 */
export function normalizeUser(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  
  const firstName = raw.firstName || raw.firstname || '';
  const lastName = raw.lastName || raw.lastname || '';
  const name = raw.name || [firstName, lastName].filter(Boolean).join(' ').trim() || '';
  const fullName = deriveFullName(raw);
  const roles = canonicalizeRoles(raw.roles || raw.role || raw.roleCode);
  
  return {
    ...raw,
    // Override with normalized values
    id: raw.id || raw._id || raw.employeeId,
    email: raw.email || raw.mail || '',
    firstName,
    lastName,
    name,
    fullName,
    roles,
  };
}
